import { IAgent, ICoordonnee, IIdentifiantAgent } from '../../lib/interface/agent'
import { affichageNomPrenom, champSansAccent } from './fonctions'

const SCORE = [1_000, 100, 10, 1] as const

const telephoneNettoye = (coordonnees: ICoordonnee) => {
  return coordonnees?.telephoneProfessionnel
    ? coordonnees.telephoneProfessionnel.trim().toLowerCase().replace(/\D+/g, '')
    : ''
}

const portableProfessionnelNettoye = (coordonnees: ICoordonnee) => {
  return coordonnees?.portableProfessionnel
    ? coordonnees.portableProfessionnel.trim().toLowerCase().replace(/\D+/g, '')
    : ''
}

const bureauNettoye = (coordonnees: ICoordonnee) => {
  return coordonnees?.bureau ? coordonnees.bureau.trim().toLowerCase() : ''
}

const idepNettoye = (identifiantsAgent: IIdentifiantAgent) => {
  return identifiantsAgent?.idep ? identifiantsAgent.idep.trim().toLowerCase() : ''
}

const calculateScore = (a: IAgent, rechercheSplit: string[]) => {
  return rechercheSplit.reduce((acc, r, index) => {
    const mult = rechercheSplit.length - index
    if (champSansAccent(a.etatCivil.nomUsage).startsWith(r)) {
      return acc + SCORE[0] * mult
    }
    if (champSansAccent(a.etatCivil.prenomUsuel).startsWith(r)) {
      return acc + SCORE[1] * mult
    }
    if (champSansAccent(affichageNomPrenom(a.etatCivil.nomUsage, a.etatCivil.prenomUsuel)).includes(r)) {
      return acc + SCORE[2] * mult
    }
    return acc + SCORE[3] * mult
  }, 0)
}

export const getAgentByRecherche = (agents: IAgent[], recherche: string) => {
  const rechercheSplit = recherche.split(' ').filter((r) => r !== '')
  if (!rechercheSplit.length) return agents

  return agents
    .filter((agent) => {
      const balf = agent.balf
      const tel = telephoneNettoye(agent.coordonnees)
      const portablePro = portableProfessionnelNettoye(agent.coordonnees)
      const bureau = bureauNettoye(agent.coordonnees)
      const idep = idepNettoye(agent.identifiantsAgent)

      return rechercheSplit.every(
        (element) =>
          champSansAccent(affichageNomPrenom(agent.etatCivil.nomUsage, agent.etatCivil.prenomUsuel)).includes(
            element
          ) ||
          tel.includes(element) ||
          portablePro.includes(element) ||
          bureau.includes(element) ||
          idep.includes(element) ||
          balf?.some(
            (item) => champSansAccent(item.nom).includes(element) || champSansAccent(item.adresse).includes(element)
          ) ||
          agent.attributions.some((attribution) => champSansAccent(attribution.libelle).includes(element))
      )
    })
    .toSorted((a, b) => calculateScore(b, rechercheSplit) - calculateScore(a, rechercheSplit))
}

const compareUnitesByTimbre = (r: string, a: INoeudDimension, b: INoeudDimension) => {
  const timbreA = a.libelle.split(' ~ ')[1] as string | undefined
  const timbreB = b.libelle.split(' ~ ')[1] as string | undefined

  // En RegEx, "\W" est tout ce qui n'est ni un caractère alphanumérique, ni "_"
  const inTimbreA = champSansAccent(timbreA)
    .split(/\W/)
    .some((str) => str.includes(r))
  const inTimbreB = champSansAccent(timbreB)
    .split(/\W/)
    .some((str) => str.includes(r))

  return Number(inTimbreB ?? 0) - Number(inTimbreA ?? 0)
}

const compareUnitesByLibelle = (r: string, a: INoeudDimension, b: INoeudDimension) => {
  const indexA = champSansAccent(a.libelle)
    .split(/\W/)
    .findIndex((str) => str.startsWith(r))
  const indexB = champSansAccent(b.libelle)
    .split(/\W/)
    .findIndex((str) => str.startsWith(r))

  if (indexA === 0 && indexB === 0) return 0
  if (indexA === 0) return -1
  if (indexB === 0) return 1
  if (indexA !== -1 && indexB !== -1) return 0
  if (indexA !== -1) return -1
  if (indexB !== -1) return 1

  return 0
}

export const searchUnite = (recherche: string, noeuds: INoeudDimension[]) => {
  const resultats = noeuds.filter((noeud: INoeudDimension) =>
    champSansAccent(noeud.libelle).includes(champSansAccent(recherche))
  )
  resultats.sort((a, b) => {
    const r = champSansAccent(recherche)
    const resultTimbre = compareUnitesByTimbre(r, a, b)
    if (resultTimbre !== 0) return resultTimbre
    const resultLibelle = compareUnitesByLibelle(r, a, b)
    if (resultLibelle !== 0) return resultLibelle
    return a.libelle.localeCompare(b.libelle)
  })
  return resultats
}
